import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";

import * as Api from "@/api";
import Avatar from "@/design/components/Avatar";
import InputPhone from "@/design/forms/InputPhone";
import { setDialNumber, setDialPbx, setDialUser } from "@/store/reducers/dial";
import { setDialDrawer } from "@/store/reducers/ui";
import Valkyrie, { viPhone } from "@sippy-platform/valkyrie";

import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

function DialDrawer() {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("lg"));
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const dialDrawer = useSelector((state) => state.ui.dialDrawer);
  const pbxStore = useSelector((state) => state.backstage.pbxes);
  const dialUserStore = useSelector((state) => state.dial.users);
  const dialPbx = useSelector((state) => state.dial.pbx);
  const dialUser = useSelector((state) => state.dial.user);
  const dialNumber = useSelector((state) => state.dial.number);

  // Get data
  useEffect(() => {
    Promise.resolve(Api.getPbxes()).then((response) => {
      if (response.data.pbxes.length === 1) {
        // Set the PBX if there is only 1
        dispatch(setDialPbx(response.data.pbxes[0]));
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (dialPbx?.context) {
      Promise.resolve(Api.getDialUsers()).then((response) => {
        if (searchParams.get("dialUser")) {
          const preselectUser = response.data.sippeers.find((user) => user.exten === searchParams.get("dialUser"));

          if (preselectUser) {
            dispatch(setDialUser(preselectUser));
          }
        }
      });
    }
  }, [dialPbx?.context, dispatch, searchParams]);

  // Calling
  const [loading, setLoading] = useState(false);

  function makeCall(e) {
    e.preventDefault();

    Promise.resolve(Api.postDialCall(dialNumber)).then(() => {
      dispatch(setDialNumber(""));
      showLoadingIndicator();
    });
  }

  function showLoadingIndicator() {
    setLoading(true);

    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }

  return (
    <Drawer
      variant={matches && dialDrawer ? "permanent" : "temporary"}
      anchor="right"
      slotProps={{
        paper: {
          elevation: 0,
          sx: [
            {
              borderTopLeftRadius: 12,
              top: 64,
              height: "calc(100dvh - 64px)",
              width: 256,
              border: "none",
              zIndex: theme.zIndex.drawer,
              transition: "width .2s ease-in-out",
              backgroundColor: "background.default",
              borderTop: `solid 1px ${theme.vars.palette.divider}`,
              borderLeft: `solid 1px ${theme.vars.palette.divider}`,
              position: { sx: "inherit", lg: "relative" },
            },
            !matches && {
              backgroundColor: (theme) =>
                `color-mix(in srgb, ${theme.vars.palette.background.default} 50%, transparent)`,
              backdropFilter: "blur(15px) saturate(125%)",
            },
          ],
        },
      }}
      onClose={() => dispatch(setDialDrawer(false))}
      open={dialDrawer}
    >
      <Stack
        sx={{
          backgroundColor: `color-mix(in srgb, ${theme.vars.palette.background.default} 75%, transparent)`,
          backdropFilter: "blur(15px) saturate(125%)",
          position: "sticky",
          top: 0,
          color: "inherit",
          border: "none",
          zIndex: 1200,
          p: 1.5,
        }}
      >
        <Typography variant="h5">Dial</Typography>
      </Stack>
      <Stack
        sx={{
          gap: 2,
          p: 1.5,
        }}
      >
        <Autocomplete
          fullWidth
          value={dialPbx ?? null}
          onChange={(e, pbx) => dispatch(setDialPbx(pbx))}
          options={pbxStore.list}
          autoHighlight
          groupBy={(option) => option.name.toUpperCase().charAt(0)}
          getOptionLabel={(option) => option?.name ?? undefined}
          isOptionEqualToValue={(option, value) => option.context === value.context}
          renderOption={(props, option) => (
            <Box
              key={option.context}
              component="li"
              sx={{
                px: "12px !important",
                "& > .MuiAvatar-root": { mr: 1, flexShrink: 0 },
              }}
              {...props}
            >
              <Avatar text={option.name} px={24} />
              {option.name}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Choose a PBX"
              slotProps={{
                ...params.slotProps,

                htmlInput: {
                  ...params.slotProps.htmlInput,
                  autoComplete: "new-password",
                },
              }}
            />
          )}
          sx={{
            "& .MuiAutocomplete-listbox": {
              py: 1,
            },
          }}
        />

        {dialPbx && (
          <Autocomplete
            fullWidth
            value={dialUser ?? null}
            onChange={(e, user) => dispatch(setDialUser(user))}
            options={dialUserStore.list}
            autoHighlight
            getOptionLabel={(option) => option?.name ?? undefined}
            isOptionEqualToValue={(option, value) => option.username === value.username}
            renderOption={(props, option) => (
              <Box
                key={option.username}
                component="li"
                sx={{
                  px: "12px !important",
                  "& > .MuiAvatar-root": { mr: 1.5, flexShrink: 0 },
                  "&:first-of-type": { mt: 1 },
                  "&:last-of-type": { mb: 1 },
                }}
                {...props}
              >
                <Avatar text={option.name} px={24} />
                <ListItemText primary={option.name} secondary={option.exten} />
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Choose a user"
                slotProps={{
                  ...params.slotProps,

                  htmlInput: {
                    ...params.slotProps.htmlInput,
                    autoComplete: "new-password",
                  },
                }}
              />
            )}
          />
        )}

        {dialUser && (
          <>
            <Divider sx={{ mx: 1 }} />
            <Stack
              sx={{
                alignItems: "center",
                textAlign: "center",
                maxWidth: 1,
              }}
            >
              <Typography variant="body" sx={{ pb: 3, opacity: 0.75 }}>
                Calling as
              </Typography>
              <Avatar text={dialUser.name} px={64} />
              <Typography variant="h4" sx={{ pt: 1 }}>
                {dialUser.name}
              </Typography>
              <Typography>{dialUser.exten}</Typography>
              <form onSubmit={(e) => makeCall(e)}>
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "flex-start",
                    gap: 0.5,
                    pt: 3,
                  }}
                >
                  <InputPhone
                    label="Phone number"
                    value={dialNumber}
                    onChange={(e, value) => dispatch(setDialNumber(value))}
                  />
                  <IconButton
                    disabled={!dialNumber || dialNumber === "+"}
                    type="submit"
                    sx={(theme) => ({
                      bgcolor: theme.vars.palette.success.light,
                      color: "#fff",
                      width: 40,
                      ":hover": {
                        bgcolor: theme.vars.palette.success.main,
                      },
                      ...theme.applyStyles("dark", {
                        backgroundColor: theme.vars.palette.success.main,
                        color: "inherit",
                        ":hover": {
                          bgcolor: theme.vars.palette.success.dark,
                        },
                      }),
                    })}
                    onClick={(e) => makeCall(e)}
                  >
                    <Valkyrie icon={viPhone} />
                  </IconButton>
                </Stack>
              </form>
              {loading && (
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <CircularProgress size="24px" />
                  <Typography>Making call...</Typography>
                </Stack>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Drawer>
  );
}

export default DialDrawer;
